import { BoxGeometry, Group, Mesh } from 'three';
import type { BasementConfig, HouseBodyConfig, StairConfig } from '@/model/schema';
import type { MaterialKit } from '../materials';

/**
 * Build the below-grade basement volume: floor slab, four solid walls,
 * and the stair flight up to the ground floor.
 *
 * Footprint
 * ---------
 * The basement has its OWN footprint (`basement.originX/Z` +
 * `footprintWidth/Depth` in body-local coords). This may be larger
 * than the house body — a perimeter porch or attached garage often
 * sits over basement space too. The generator already clamped this
 * footprint inside the lot.
 *
 * Positioning
 * -----------
 * The returned group sits at world y = 0 (ground level). Everything
 * inside is translated DOWN into negative y so the basement's ceiling
 * butts against the ground-floor slab underside (well, against the
 * lot base plate underside outside the body footprint, and against
 * the L0 slab inside the body footprint). The stair's bottom tread
 * sits on the basement floor; the top tread lines up with the L0
 * floor's top surface.
 *
 * Material strategy
 * -----------------
 * The basement floor uses `kit.basement` (concrete grey) NOT
 * `kit.basePlate`. With the same material as the lot ground above,
 * the user couldn't tell where the L0 slab hole opened into the
 * basement well — it just looked continuous.
 */
export function buildBasement(
  body: HouseBodyConfig,
  basement: BasementConfig,
  kit: MaterialKit,
): Group {
  const group = new Group();
  group.name = 'Basement';

  const w = basement.footprintWidth;
  const d = basement.footprintDepth;
  const h = basement.height;
  const floorT = basement.floorThickness;
  const wallT = basement.wallThickness;

  // World-space center of the basement footprint:
  //   bodyOrigin + basement.origin + half-extent
  const cx = body.originX + basement.originX + w / 2;
  const cz = body.originZ + basement.originZ + d / 2;

  // Floor slab bottom sits at -(h + floorT). Centre the slab on the
  // basement footprint X/Z. We shrink the floor INWARD by `wallT` on
  // each side so the floor's vertical sides don't share a plane with
  // the basement walls' outer faces — without this the two coplanar
  // surfaces flicker (z-fight) along every basement perimeter edge,
  // user-reported in the latest pass.
  const floorW = Math.max(0.1, w - 2 * wallT);
  const floorD = Math.max(0.1, d - 2 * wallT);
  const floor = new Mesh(new BoxGeometry(floorW, floorT, floorD), kit.basement);
  floor.name = 'FloorSlab';
  floor.castShadow = false;
  floor.receiveShadow = true;
  floor.position.set(cx, -h - floorT / 2, cz);
  group.add(floor);

  // Walls: one box per side, on the OUTER edge of the basement footprint
  // so the outer face is flush with the basement perimeter. The wall TOP
  // must stop strictly below grade — otherwise (the previous +0.01 m
  // overshoot) the wall pokes up through the lot base plate where the
  // basement extends past the body footprint, producing a visible
  // concrete strip on the lawn (user-reported). We park the top 5 mm
  // below grade, which is well inside any sane base-plate thickness so
  // the cap is hidden by the opaque plate above.
  const wallsGroup = new Group();
  wallsGroup.name = 'Walls';
  group.add(wallsGroup);
  const wallTopY = -0.005;
  const wallBottomY = -h - floorT;
  const wallHeight = wallTopY - wallBottomY;
  const wallY = (wallTopY + wallBottomY) / 2;
  const baseWorldX = body.originX + basement.originX;
  const baseWorldZ = body.originZ + basement.originZ;

  const zWall = (sz: number, zC: number, name: string) => {
    const m = new Mesh(new BoxGeometry(w, wallHeight, sz), kit.basementWall);
    m.name = name;
    m.castShadow = false;
    m.receiveShadow = true;
    m.position.set(cx, wallY, zC);
    wallsGroup.add(m);
  };
  zWall(wallT, baseWorldZ + wallT / 2, 'Wall_south');
  zWall(wallT, baseWorldZ + d - wallT / 2, 'Wall_north');

  const interiorDepth = d - 2 * wallT;
  const xWall = (sx: number, xC: number, name: string) => {
    const m = new Mesh(new BoxGeometry(sx, wallHeight, interiorDepth), kit.basementWall);
    m.name = name;
    m.castShadow = false;
    m.receiveShadow = true;
    m.position.set(xC, wallY, cz);
    wallsGroup.add(m);
  };
  xWall(wallT, baseWorldX + wallT / 2, 'Wall_west');
  xWall(wallT, baseWorldX + w - wallT / 2, 'Wall_east');

  group.add(buildBasementStair(body, basement.stair, basement, kit));

  return group;
}

const TARGET_STEP_RISE = 0.175;
const MIN_TREAD_DEPTH = 0.25;
const TREAD_THICKNESS = 0.04;

function buildBasementStair(
  body: HouseBodyConfig,
  stair: StairConfig,
  basement: BasementConfig,
  kit: MaterialKit,
): Group {
  const flight = new Group();
  flight.name = 'Stairs_to_L0';

  const lowerFloorTop = -basement.height; // top of the basement floor slab
  const upperFloorTop = body.floorThickness; // top of the L0 floor slab
  const climbHeight = upperFloorTop - lowerFloorTop;

  const climbAxisLen =
    stair.climbDir === 'plusX' || stair.climbDir === 'minusX' ? stair.width : stair.depth;
  const lateralLen =
    stair.climbDir === 'plusX' || stair.climbDir === 'minusX' ? stair.depth : stair.width;
  const riseBasedCount = Math.max(2, Math.round(climbHeight / TARGET_STEP_RISE));
  const depthBasedMax = Math.max(2, Math.floor(climbAxisLen / MIN_TREAD_DEPTH));
  const count = Math.min(riseBasedCount, depthBasedMax);
  const stepDepth = climbAxisLen / count;
  const stepRise = climbHeight / count;

  for (let i = 0; i < count; i++) {
    const isLateralX = stair.climbDir === 'plusX' || stair.climbDir === 'minusX';
    const tread = new Mesh(
      new BoxGeometry(
        isLateralX ? stepDepth : lateralLen,
        TREAD_THICKNESS,
        isLateralX ? lateralLen : stepDepth,
      ),
      kit.stair,
    );
    tread.name = `Tread_${i.toString().padStart(3, '0')}`;
    tread.castShadow = true;
    tread.receiveShadow = true;
    const yCentre = lowerFloorTop + (i + 1) * stepRise - TREAD_THICKNESS / 2;
    let xCentre = stair.x + stair.width / 2;
    let zCentre = stair.z + stair.depth / 2;
    switch (stair.climbDir) {
      case 'plusX':
        xCentre = stair.x + (i + 0.5) * stepDepth;
        break;
      case 'minusX':
        xCentre = stair.x + stair.width - (i + 0.5) * stepDepth;
        break;
      case 'plusZ':
        zCentre = stair.z + (i + 0.5) * stepDepth;
        break;
      case 'minusZ':
        zCentre = stair.z + stair.depth - (i + 0.5) * stepDepth;
        break;
    }
    tread.position.set(body.originX + xCentre, yCentre, body.originZ + zCentre);
    flight.add(tread);
  }

  return flight;
}
